import ExpandMore from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, styled, Typography } from '@mui/material';
import { CouponDefinitionDTO } from 'probonio-shared-ui/api';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  coupons?: CouponDefinitionDTO[];
  amount?: number;
  showAmount?: boolean;
  isIncentive?: boolean;
}

const StyledTypography = styled(Typography)(({ theme }) => {
  return {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  };
});

const isAvailableCoupon = (monthlyBudget: number, coupon: CouponDefinitionDTO): boolean => {
  const variant = coupon.variations?.find(variation => variation === monthlyBudget);
  return variant !== undefined;
};
const isCombinationCoupon = (monthlyBudget: number, coupon: CouponDefinitionDTO): boolean => {
  const combination = coupon.combinations?.find(combination => combination === monthlyBudget);
  return combination !== undefined;
};

//use this on NOT available coupons
const isCouponSplittableForAmount = (variations: number[] | undefined, total: number): boolean => {
  if (!variations?.length) {
    return false;
  }

  const sortedVariations = [...variations].sort((a, b) => b - a);
  const filteredVariations = sortedVariations.filter(value => value % 1_00 === 0);
  if (total % 50_00 === 0 && filteredVariations.includes(50_00)) {
    return true;
  }

  function backtrack(
    rest: number,
    index: number,
    combination: { amount: number; count: number }[],
  ): null | { amount: number; count: number }[] {
    if (rest === 0) {
      return combination;
    }
    if (index >= filteredVariations!.length) {
      return null;
    }

    const amount = filteredVariations![index];
    const maxCount = Math.floor(rest / amount);

    for (let count = maxCount; count >= 0; count--) {
      const newCombo = [...combination];
      if (count > 0) {
        newCombo.push({ amount, count });
      }

      const result = backtrack(rest - amount * count, index + 1, newCombo);
      if (result) {
        return result;
      }
    }

    return null;
  }

  return !!backtrack(total, 0, [])?.length;
};

export const AvailableCouponsAccordion: React.FC<Props> = ({ coupons, amount, showAmount, isIncentive }) => {
  const { t } = useTranslation('benefitsModule');

  const [availableCoupons, combinationCoupons, unavailableCoupons] = useMemo(() => {
    const availableCoupons: CouponDefinitionDTO[] = [];
    const combinationCoupons: CouponDefinitionDTO[] = [];
    const unavailableCoupons: CouponDefinitionDTO[] = [];

    coupons?.forEach(coupon => {
      if (isAvailableCoupon(amount || 0, coupon)) {
        availableCoupons.push(coupon);
      } else if (isIncentive && amount && amount > 60_00 && isCouponSplittableForAmount(coupon.variations, amount)) {
        combinationCoupons.push(coupon);
      } else if (isCombinationCoupon(amount || 0, coupon)) {
        combinationCoupons.push(coupon);
      } else {
        unavailableCoupons.push(coupon);
      }
    });
    return [availableCoupons, combinationCoupons, unavailableCoupons];
  }, [coupons, amount, isIncentive]);

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h3" color="text.secondary">
          {t(showAmount ? 'COUPONS.availableTitleWithAmount' : 'COUPONS.availableTitle', {
            amount,
            count: (availableCoupons?.length || 0) + (combinationCoupons?.length || 0),
          })}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <StyledTypography paddingBottom={1.5}>
          <u>{t('COUPONS.available')}</u> {availableCoupons?.map(coupon => coupon.name).join(', ')}
        </StyledTypography>
        {!!combinationCoupons?.length && (
          <StyledTypography paddingBottom={1.5}>
            <u>{t('COUPONS.split')}</u> {combinationCoupons?.map(coupon => coupon.name).join(', ')}
          </StyledTypography>
        )}
        {!!unavailableCoupons?.length && (
          <StyledTypography>
            <u>{t('COUPONS.unavailable')}</u> {unavailableCoupons?.map(coupon => coupon.name).join(', ')}
          </StyledTypography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
